import React, { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';
import { z, emailAddress, trueOnly } from '~/common/utils/validations';
import { Button, Input, Checkbox } from '@ui-elements';
import { PrivacyPanel } from '../privacy-panel';
import { PrivacyToggle } from '../privacy-toggle';
import { StepOneValues } from '../../types';
import styles from './index.module.scss';

const schema = z.object({
  email: emailAddress('Please enter a valid email address'),
  privacyAccepted: trueOnly(
    'You must agree to the Privacy Collection Statement.'
  ),
});

interface StepOneProps {
  title?: string;
  content?: string;
  privacyCheckbox?: string;
  privacyDropDown?: string;
  fontColor?: string;
  contentAlignment?: any;
  onNext: (email: string) => void;
}

export const StepOne: React.FC<StepOneProps> = ({
  title,
  content,
  privacyCheckbox,
  privacyDropDown,
  fontColor,
  contentAlignment,
  onNext,
}) => {
  const [isPrivacyPanelOpen, setIsPrivacyPanelOpen] = useState(false);

  const { control, handleSubmit, formState } = useForm<StepOneValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { email: '', privacyAccepted: false },
  });

  const onSubmit = (values: StepOneValues) => onNext(values.email);

  return (
    <div className={styles.StepOne}>
      <h2 style={{ color: fontColor }}>{title}</h2>
      <div
        dangerouslySetInnerHTML={{ __html: content as string }}
        style={{ textAlign: !!content ? contentAlignment : 'center' }}
      />

      <div className={styles.FormBlock}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                type="email"
                placeholder="Enter your email address…"
                error={formState.errors.email?.message}
                errorColor="#b00020"
              />
            )}
          />

          <Controller
            name="privacyAccepted"
            control={control}
            render={({ field }) => (
              <Checkbox
                {...field}
                className={styles.PrivacyCheckbox}
                error={formState.errors.privacyAccepted?.message}
                errorColor="#b00020"
              >
                <span
                  dangerouslySetInnerHTML={{ __html: privacyCheckbox || '' }}
                />
              </Checkbox>
            )}
          />

          <Button
            nativeType="submit"
            buttonSize="medium"
            disabled={!formState.isValid}
          >
            Subscribe
          </Button>
        </form>

        <PrivacyToggle
          isOpen={isPrivacyPanelOpen}
          onClick={() => setIsPrivacyPanelOpen((isOpen) => !isOpen)}
        />
      </div>

      {isPrivacyPanelOpen && <PrivacyPanel html={privacyDropDown} />}
    </div>
  );
};
